import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Modal } from "react-bootstrap";
import { buildApiUrl } from "../../config/api";
import styles from "./AdminPanel.module.css";

const STORAGE_KEY = "admin_panel_token";
const PLATFORM_FIELDS = [
  { type: "SPOTIFY", label: "Spotify" },
  { type: "YOUTUBE", label: "YouTube" },
  { type: "SOUNDCLOUD", label: "SoundCloud" },
  { type: "APPLE", label: "Apple Music" },
  { type: "YTMUSIC", label: "YouTube Music" },
  { type: "AMZNMUSIC", label: "Amazon Music" }
];

function createEmptyPlatforms() {
  return PLATFORM_FIELDS.reduce((accumulator, field) => {
    accumulator[field.type] = "";
    return accumulator;
  }, {});
}

function getStoredToken() {
  return sessionStorage.getItem(STORAGE_KEY) || "";
}

function buildAdminHeaders(token) {
  return {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
}

export default function AdminPanel() {
  const [token, setToken] = useState(getStoredToken());
  const [status, setStatus] = useState("");
  const [statusType, setStatusType] = useState("info");
  const [copiedLink, setCopiedLink] = useState("");
  const [login, setLogin] = useState({ username: "admin", password: "" });
  const [activeTab, setActiveTab] = useState("songs");
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [songs, setSongs] = useState([]);
  const [videos, setVideos] = useState([]);
  const [songSearch, setSongSearch] = useState("");
  const [isSongModalOpen, setIsSongModalOpen] = useState(false);
  const [songMode, setSongMode] = useState("create");
  const [songForm, setSongForm] = useState({
    id: "",
    title: "",
    interpret: "",
    coverImageUrl: "",
    album: "",
    releaseBool: 0,
    videoBool: 0,
    state: "ACTIVE",
    ytId: "",
    platforms: createEmptyPlatforms()
  });
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [videoMode, setVideoMode] = useState("create");
  const [videoForm, setVideoForm] = useState({ id: "", title: "", interpret: "", ytId: "" });

  const isAuthenticated = useMemo(() => Boolean(token), [token]);

  const filteredSongs = useMemo(() => {
    const query = songSearch.trim().toLowerCase();
    if (!query) {
      return songs;
    }

    return songs.filter((song) =>
      String(song.id).toLowerCase().includes(query) ||
      String(song.title).toLowerCase().includes(query) ||
      String(song.interpret).toLowerCase().includes(query) ||
      String(song.album).toLowerCase().includes(query)
    );
  }, [songSearch, songs]);

  useEffect(() => {
    if (isAuthenticated) {
      void loadDashboardData();
    }
  }, [isAuthenticated]);

  function setFeedback(message, type = "info") {
    setStatus(message);
    setStatusType(type);
  }

  function resetSongForm() {
    setSongMode("create");
    setSongForm({
      id: "",
      title: "",
      interpret: "",
      coverImageUrl: "",
      album: "",
      releaseBool: 0,
      videoBool: 0,
      state: "ACTIVE",
      ytId: "",
      platforms: createEmptyPlatforms()
    });
    setIsSongModalOpen(false);
  }

  function resetVideoForm() {
    setVideoMode("create");
    setVideoForm({ id: "", title: "", interpret: "", ytId: "" });
    setIsVideoModalOpen(false);
  }

  function normalizeSongId(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  function getSongShareUrl(songId) {
    return `${window.location.origin}/song/${songId}`;
  }

  function getVideoShareUrl(video) {
    return video?.ytId ? `https://www.youtube.com/watch?v=${video.ytId}` : "";
  }

  function buildSongPlatformsPayload(platforms) {
    return PLATFORM_FIELDS.map((field) => ({
      type: field.type,
      songUrl: platforms?.[field.type] || ""
    }));
  }

  async function copyText(value, successMessage) {
    if (!value) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      setCopiedLink(value);
      setFeedback(successMessage, "success");
    } catch {
      setFeedback("Kopieren fehlgeschlagen.", "error");
    }
  }

  async function loadDashboardData() {
    setIsLoadingData(true);
    setFeedback("");

    try {
      const [songResponse, videoResponse] = await Promise.all([
        axios.get(buildApiUrl("/api/track/all"), buildAdminHeaders(token)),
        axios.get(buildApiUrl("/api/video/all"), buildAdminHeaders(token))
      ]);

      setSongs(Array.isArray(songResponse.data) ? songResponse.data : []);
      setVideos(Array.isArray(videoResponse.data) ? videoResponse.data : []);
    } catch (error) {
      setFeedback(`Daten laden fehlgeschlagen: ${error?.response?.data?.error || error.message}`, "error");
    } finally {
      setIsLoadingData(false);
    }
  }

  async function handleLogin(event) {
    event.preventDefault();
    setFeedback("");

    try {
      const response = await axios.post(buildApiUrl("/api/admin/login"), login);
      const receivedToken = response.data?.token;
      if (!receivedToken) {
        throw new Error("Token missing in response.");
      }

      sessionStorage.setItem(STORAGE_KEY, receivedToken);
      setToken(receivedToken);
      setFeedback("Login erfolgreich.", "success");
      setLogin((prev) => ({ ...prev, password: "" }));
    } catch (error) {
      setFeedback(`Login fehlgeschlagen: ${error?.response?.data?.error || error.message}`, "error");
    }
  }

  function handleLogout() {
    sessionStorage.removeItem(STORAGE_KEY);
    setToken("");
    setSongs([]);
    setVideos([]);
    setCopiedLink("");
    resetSongForm();
    resetVideoForm();
    setFeedback("Abgemeldet.", "success");
  }

  async function handleSubmitSong(event) {
    event.preventDefault();
    setFeedback("");
    setIsSubmitting(true);

    try {
      const generatedId = normalizeSongId(songForm.title);
      const id = String(songForm.id || "").trim() || generatedId;
      if (!id) {
        throw new Error("Song-ID konnte nicht erzeugt werden. Bitte Titel oder ID angeben.");
      }

      const payload = {
        ...songForm,
        id,
        releaseBool: Number(songForm.releaseBool),
        videoBool: Number(songForm.videoBool),
        platforms: buildSongPlatformsPayload(songForm.platforms)
      };

      if (songMode === "edit") {
        await axios.put(buildApiUrl(`/api/track/${songForm.id}`), payload, buildAdminHeaders(token));
        setFeedback("Song aktualisiert.", "success");
      } else {
        await axios.post(buildApiUrl("/api/track"), payload, buildAdminHeaders(token));
        setFeedback("Song erstellt.", "success");
      }

      await loadDashboardData();
      resetSongForm();
    } catch (error) {
      setFeedback(`Song speichern fehlgeschlagen: ${error?.response?.data?.error || error.message}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDeleteSong(songId) {
    const shouldDelete = window.confirm(`Song ${songId} wirklich löschen?`);
    if (!shouldDelete) {
      return;
    }

    setFeedback("");
    setIsSubmitting(true);

    try {
      await axios.delete(buildApiUrl(`/api/track/${songId}`), buildAdminHeaders(token));
      setFeedback("Song gelöscht.", "success");
      await loadDashboardData();

      if (songForm.id === songId) {
        resetSongForm();
      }
    } catch (error) {
      setFeedback(`Song löschen fehlgeschlagen: ${error?.response?.data?.error || error.message}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleEditSong(song) {
    setSongMode("edit");
    setSongForm({
      id: String(song.id || ""),
      title: song.title || "",
      interpret: song.interpret || "",
      coverImageUrl: song.coverImageUrl || "",
      album: song.album || "",
      releaseBool: Number(song.releaseBool || 0),
      videoBool: Number(song.videoBool || 0),
      state: song.state || "ACTIVE",
      ytId: song.ytId || "",
      platforms: createEmptyPlatforms()
    });
    setActiveTab("songs");
    setIsSongModalOpen(true);

    void (async () => {
      try {
        const response = await axios.get(buildApiUrl(`/api/track/${song.id}`));
        const platformMap = createEmptyPlatforms();
        for (const platform of response.data?.platforms || []) {
          if (platformMap[platform.type] !== undefined) {
            platformMap[platform.type] = platform.songUrl || "";
          }
        }
        setSongForm((prev) => ({ ...prev, platforms: platformMap }));
      } catch {
        // Keep the editable modal open even if platform details fail to load.
      }
    })();
  }

  async function handleSubmitVideo(event) {
    event.preventDefault();
    setFeedback("");
    setIsSubmitting(true);

    try {
      const payload = {
        title: videoForm.title,
        interpret: videoForm.interpret,
        ytId: videoForm.ytId
      };

      if (videoMode === "edit") {
        await axios.put(buildApiUrl(`/api/video/${videoForm.id}`), payload, buildAdminHeaders(token));
        setFeedback("Video aktualisiert.", "success");
      } else {
        await axios.post(buildApiUrl("/api/video"), payload, buildAdminHeaders(token));
        setFeedback("Video erstellt.", "success");
      }

      await loadDashboardData();
      resetVideoForm();
    } catch (error) {
      setFeedback(`Video speichern fehlgeschlagen: ${error?.response?.data?.error || error.message}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleEditVideo(video) {
    setVideoMode("edit");
    setVideoForm({
      id: String(video.id || ""),
      title: video.title || "",
      interpret: video.interpret || "",
      ytId: video.ytId || ""
    });
    setActiveTab("videos");
    setIsVideoModalOpen(true);
  }

  async function handleDeleteVideo(videoId) {
    const shouldDelete = window.confirm(`Video ${videoId} wirklich löschen?`);
    if (!shouldDelete) {
      return;
    }

    setFeedback("");
    setIsSubmitting(true);

    try {
      await axios.delete(buildApiUrl(`/api/video/${videoId}`), buildAdminHeaders(token));
      setFeedback("Video gelöscht.", "success");
      await loadDashboardData();

      if (String(videoForm.id) === String(videoId)) {
        resetVideoForm();
      }
    } catch (error) {
      setFeedback(`Video löschen fehlgeschlagen: ${error?.response?.data?.error || error.message}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  function openCreateModal() {
    if (activeTab === "songs") {
      resetSongForm();
      setSongMode("create");
      setIsSongModalOpen(true);
      return;
    }

    resetVideoForm();
    setVideoMode("create");
    setIsVideoModalOpen(true);
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <div className={styles.headerRow}>
          <div className={styles.headerMain}>
            <h1 className={styles.headline}>Admin Panel</h1>
            <p className={styles.subline}>Verwaltung von Songs und Videos in einer klaren, reduzierten Oberfläche.</p>
          </div>
          {isAuthenticated && (
            <button className={styles.secondaryBtn} type="button" onClick={handleLogout}>Logout</button>
          )}
        </div>

        {!isAuthenticated && (
          <form className={styles.card} onSubmit={handleLogin}>
            <h2>Admin Login</h2>
            <div className={styles.formGrid}>
              <input
                type="text"
                placeholder="Username"
                value={login.username}
                onChange={(e) => setLogin((prev) => ({ ...prev, username: e.target.value }))}
                required
              />
              <input
                type="password"
                placeholder="Password"
                value={login.password}
                onChange={(e) => setLogin((prev) => ({ ...prev, password: e.target.value }))}
                required
              />
            </div>
            <div className={styles.loginActions}>
              <button type="submit">Login</button>
            </div>
          </form>
        )}

        {isAuthenticated && (
          <>
            <div className={styles.toolbar}>
              <div className={styles.toolbarLeft}>
                <p className={styles.toolbarLabel}>Inhalte</p>
                <div className={styles.tabButtons}>
                  <button
                    type="button"
                    className={activeTab === "songs" ? styles.tabActive : styles.tabButton}
                    onClick={() => setActiveTab("songs")}
                  >
                    Songs ({songs.length})
                  </button>
                  <button
                    type="button"
                    className={activeTab === "videos" ? styles.tabActive : styles.tabButton}
                    onClick={() => setActiveTab("videos")}
                  >
                    Videos ({videos.length})
                  </button>
                </div>
              </div>
              <div className={styles.toolbarActions}>
                <button type="button" className={styles.secondaryBtn} onClick={loadDashboardData}>Aktualisieren</button>
                <button type="button" className={styles.primaryBtn} onClick={openCreateModal}>
                  Neu anlegen
                </button>
              </div>
            </div>

            {activeTab === "songs" && (
              <>
                <div className={styles.card}>
                  <div className={styles.sectionHeader}>
                    <h2>Song-Liste</h2>
                    <input
                      className={styles.searchInput}
                      placeholder="Suchen nach ID, Titel, Interpret, Album"
                      value={songSearch}
                      onChange={(e) => setSongSearch(e.target.value)}
                    />
                  </div>
                  <div className={styles.tableWrap}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>Cover</th>
                          <th>Titel</th>
                          <th>Interpret</th>
                          <th>Status</th>
                          <th>Release</th>
                          <th>Video</th>
                          <th className={styles.actionsHeader}>Aktionen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSongs.map((song) => (
                          <tr key={song.id}>
                            <td>
                              <img className={styles.coverThumb} src={song.coverImageUrl} alt={song.title} />
                            </td>
                            <td>
                              <a
                                className={styles.songLink}
                                href={getSongShareUrl(song.id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Song öffnen"
                              >
                                {song.title}
                              </a>
                              <div className={styles.metaCell}>{song.id}</div>
                            </td>
                            <td>{song.interpret}</td>
                            <td>{song.state}</td>
                            <td>{Number(song.releaseBool) === 1 ? "Ja" : "Nein"}</td>
                            <td>{Number(song.videoBool) === 1 ? "Ja" : "Nein"}</td>
                            <td className={styles.actionsCell}>
                              <div className={styles.rowActions}>
                                <button type="button" className={styles.iconBtn} onClick={() => window.open(getSongShareUrl(song.id), "_blank", "noopener,noreferrer")} aria-label="Song öffnen" title="Song öffnen">
                                  <i className="bi bi-box-arrow-up-right" />
                                </button>
                                <button type="button" className={styles.iconBtn} onClick={() => copyText(getSongShareUrl(song.id), "Song-Link kopiert.")} aria-label="Song-Link kopieren" title="Song-Link kopieren">
                                  <i className="bi bi-link-45deg" />
                                </button>
                                <button type="button" className={styles.iconBtn} onClick={() => handleEditSong(song)} aria-label="Song bearbeiten" title="Song bearbeiten">
                                  <i className="bi bi-pencil" />
                                </button>
                                <button type="button" className={styles.iconBtnDanger} onClick={() => handleDeleteSong(song.id)} aria-label="Song löschen" title="Song löschen">
                                  <i className="bi bi-trash3" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {activeTab === "videos" && (
              <>
                <div className={styles.card}>
                  <div className={styles.sectionHeader}>
                    <h2>Video-Liste</h2>
                  </div>
                  <div className={styles.tableWrap}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Titel</th>
                          <th>Interpret</th>
                          <th>YouTube ID</th>
                          <th className={styles.actionsHeader}>Aktionen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {videos.map((video) => (
                          <tr key={video.id}>
                            <td>{video.id}</td>
                            <td>
                              <a
                                className={styles.songLink}
                                href={getVideoShareUrl(video)}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Video öffnen"
                              >
                                {video.title}
                              </a>
                            </td>
                            <td>{video.interpret}</td>
                            <td>{video.ytId}</td>
                            <td className={styles.actionsCell}>
                              <div className={styles.rowActions}>
                                <button type="button" className={styles.iconBtn} onClick={() => window.open(getVideoShareUrl(video), "_blank", "noopener,noreferrer")} aria-label="Video öffnen" title="Video öffnen">
                                  <i className="bi bi-box-arrow-up-right" />
                                </button>
                                <button type="button" className={styles.iconBtn} onClick={() => copyText(getVideoShareUrl(video), "Video-Link kopiert.")} aria-label="Video-Link kopieren" title="Video-Link kopieren">
                                  <i className="bi bi-link-45deg" />
                                </button>
                                <button type="button" className={styles.iconBtn} onClick={() => handleEditVideo(video)} aria-label="Video bearbeiten" title="Video bearbeiten">
                                  <i className="bi bi-pencil" />
                                </button>
                                <button type="button" className={styles.iconBtnDanger} onClick={() => handleDeleteVideo(video.id)} aria-label="Video löschen" title="Video löschen">
                                  <i className="bi bi-trash3" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {isLoadingData && <p className={styles.status}>Lade Daten...</p>}
          </>
        )}

        <Modal show={isSongModalOpen} onHide={resetSongForm} centered dialogClassName={styles.modalDialog} contentClassName={styles.modalContent}>
          <Modal.Header closeButton className={styles.modalHeader}>
            <Modal.Title>{songMode === "edit" ? "Song bearbeiten" : "Song anlegen"}</Modal.Title>
          </Modal.Header>
          <form onSubmit={handleSubmitSong}>
            <Modal.Body className={styles.modalBody}>
              <div className={styles.formGrid}>
                <input
                  placeholder="Song ID"
                  value={songForm.id}
                  onChange={(e) => setSongForm((prev) => ({ ...prev, id: e.target.value }))}
                  readOnly={songMode === "edit"}
                />
                <input placeholder="Titel" value={songForm.title} onChange={(e) => setSongForm((p) => ({ ...p, title: e.target.value }))} required />
                <input placeholder="Interpret" value={songForm.interpret} onChange={(e) => setSongForm((p) => ({ ...p, interpret: e.target.value }))} required />
                <input placeholder="Cover URL" value={songForm.coverImageUrl} onChange={(e) => setSongForm((p) => ({ ...p, coverImageUrl: e.target.value }))} required />
                <input placeholder="Album" value={songForm.album} onChange={(e) => setSongForm((p) => ({ ...p, album: e.target.value }))} required />
                <select value={songForm.releaseBool} onChange={(e) => setSongForm((p) => ({ ...p, releaseBool: Number(e.target.value) }))}>
                  <option value={0}>Kein Release</option>
                  <option value={1}>Release</option>
                </select>
                <select value={songForm.videoBool} onChange={(e) => setSongForm((p) => ({ ...p, videoBool: Number(e.target.value) }))}>
                  <option value={0}>Kein Video</option>
                  <option value={1}>Video</option>
                </select>
                <select value={songForm.state} onChange={(e) => setSongForm((p) => ({ ...p, state: e.target.value }))}>
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </select>
                <input placeholder="YouTube ID" value={songForm.ytId} onChange={(e) => setSongForm((p) => ({ ...p, ytId: e.target.value }))} />
              </div>
              <div className={styles.platformSection}>
                <div className={styles.platformSectionTitle}>Plattform-Links</div>
                <div className={styles.platformGrid}>
                  {PLATFORM_FIELDS.map((field) => (
                    <div key={field.type} className={styles.platformField}>
                      <label>{field.label}</label>
                      <input
                        placeholder={`${field.label} URL`}
                        value={songForm.platforms[field.type]}
                        onChange={(e) => setSongForm((prev) => ({
                          ...prev,
                          platforms: {
                            ...prev.platforms,
                            [field.type]: e.target.value
                          }
                        }))}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer className={styles.modalFooter}>
              <button type="button" className={styles.secondaryBtn} onClick={resetSongForm}>Abbrechen</button>
              <button type="submit" className={styles.primaryBtn} disabled={isSubmitting}>{songMode === "edit" ? "Speichern" : "Anlegen"}</button>
            </Modal.Footer>
          </form>
        </Modal>

        <Modal show={isVideoModalOpen} onHide={resetVideoForm} centered dialogClassName={styles.modalDialog} contentClassName={styles.modalContent}>
          <Modal.Header closeButton className={styles.modalHeader}>
            <Modal.Title>{videoMode === "edit" ? "Video bearbeiten" : "Video anlegen"}</Modal.Title>
          </Modal.Header>
          <form onSubmit={handleSubmitVideo}>
            <Modal.Body className={styles.modalBody}>
              <div className={styles.formGrid}>
                <input placeholder="Titel" value={videoForm.title} onChange={(e) => setVideoForm((p) => ({ ...p, title: e.target.value }))} required />
                <input placeholder="Interpret" value={videoForm.interpret} onChange={(e) => setVideoForm((p) => ({ ...p, interpret: e.target.value }))} required />
                <input placeholder="YouTube ID" value={videoForm.ytId} onChange={(e) => setVideoForm((p) => ({ ...p, ytId: e.target.value }))} required />
              </div>
            </Modal.Body>
            <Modal.Footer className={styles.modalFooter}>
              <button type="button" className={styles.secondaryBtn} onClick={resetVideoForm}>Abbrechen</button>
              <button type="submit" className={styles.primaryBtn} disabled={isSubmitting}>{videoMode === "edit" ? "Speichern" : "Anlegen"}</button>
            </Modal.Footer>
          </form>
        </Modal>

        {status && (
          <p
            className={`${styles.status} ${
              statusType === "error" ? styles.statusError : statusType === "success" ? styles.statusSuccess : ""
            }`}
          >
            {status}
          </p>
        )}

        {copiedLink && <div className={styles.copyHint}>Link bereit: {copiedLink}</div>}
      </div>
    </div>
  );
}
